// kitchen-alarm — module-level audible alarm singleton + active-state flag.
// Shared between the timer ringer (KitchenAlarmRinger in app.tsx) and the
// wake-handler / stop-keyword logic in quick-chat.tsx. Exporting from a
// dedicated module avoids a circular import between those two files.

let _stop: (() => void) | null = null;
let _active = false;
const _listeners = new Set<(active: boolean) => void>();

/** Loud repeating two-tone square wave until the user taps (any pointer-
 *  down anywhere on the page) or 60 s passes. Auto-resumes a suspended
 *  AudioContext so it works without a fresh user gesture. */
export function playKitchenAlarm(): void {
  if (_stop) { _stop(); _stop = null; }
  type WAud = Window & { webkitAudioContext?: typeof AudioContext };
  const AudioCtx = window.AudioContext || (window as WAud).webkitAudioContext;
  if (!AudioCtx) return;
  const ctx = new AudioCtx();
  const gain = ctx.createGain();
  gain.gain.value = 0.55;
  gain.connect(ctx.destination);
  let stopped = false;
  let beepCount = 0;
  const beep = () => {
    if (stopped) return;
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(beepCount % 2 === 0 ? 880 : 660, ctx.currentTime);
    osc.connect(gain);
    osc.start();
    osc.stop(ctx.currentTime + 0.32);
    beepCount++;
    window.setTimeout(beep, 480);
  };
  const start = async () => {
    try { if (ctx.state === "suspended") await ctx.resume(); } catch { /* ignore */ }
    beep();
  };
  void start();
  const stop = () => {
    if (stopped) return;
    stopped = true;
    try { ctx.close(); } catch { /* ignore */ }
    window.removeEventListener("pointerdown", stop, true);
    window.removeEventListener("keydown", stop, true);
    _stop = null;
    _active = false;
    _listeners.forEach((fn) => { try { fn(false); } catch { /* swallow */ } });
  };
  // Auto-stop after 60 s so a missed kitchen doesn't blare forever.
  window.setTimeout(stop, 60_000);
  window.addEventListener("pointerdown", stop, true);
  window.addEventListener("keydown", stop, true);
  _stop = stop;
  _active = true;
  _listeners.forEach((fn) => { try { fn(true); } catch { /* swallow */ } });
}

export function stopKitchenAlarm(): void { _stop?.(); }
export function isKitchenAlarmActive(): boolean { return _active; }

/** Subscribe to alarm-active state changes. Returns unsubscribe. */
export function onKitchenAlarmActive(fn: (active: boolean) => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}
